
import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const LeadCaptureForm = ({ formId, title, subtitle }: { formId: string; title: string; subtitle: string }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyCondition: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('propertyCondition', formData.propertyCondition);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d67802ej853lk6gjupeg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">
          We've received your information and will connect you with a trusted El Paso realtor within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form 
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Property Address *</label>
          <input
            type="text"
            name="propertyAddress"
            required
            value={formData.propertyAddress}
            onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="123 Main St, El Paso, TX 79901"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Condition</label>
            <select
              name="propertyCondition"
              value={formData.propertyCondition}
              onChange={(e) => setFormData({ ...formData, propertyCondition: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select condition</option>
              <option value="Excellent - Move-in Ready">Excellent - Move-in Ready</option>
              <option value="Good - Minor Updates Needed">Good - Minor Updates Needed</option>
              <option value="Fair - Some Repairs Needed">Fair - Some Repairs Needed</option>
              <option value="Needs Work - Major Repairs">Needs Work - Major Repairs</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timeline to Sell</label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="ASAP - Within 30 days">ASAP - Within 30 days</option>
              <option value="1-3 months">1-3 months</option>
              <option value="3-6 months">3-6 months</option>
              <option value="6+ months">6+ months</option>
              <option value="Just exploring options">Just exploring options</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Tell us about your property and what you're looking for..."
          />
          <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get Connected with a Top Realtor'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          By submitting, you agree to be contacted about real estate services. Your information is secure and will never be sold.
        </p>
      </form>
    </div>
  );
};

export default function HireRealtorElPasoPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Hire a Realtor in El Paso TX | Top Real Estate Agents | TREX RE LLC"
        description="Looking to hire a realtor in El Paso? Get connected with experienced, trusted real estate agents who know the local market. Free realtor referral service for El Paso homeowners wanting top dollar for their property."
        keywords="hire realtor El Paso, El Paso real estate agent, find realtor El Paso TX, best realtors El Paso, real estate agents near me, sell house El Paso realtor"
        canonical="/elpaso-tx/hire-realtor-el-paso"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Looking to Hire a Realtor in El Paso? Here's What You Need to Know",
          description: "Complete guide to finding and hiring the right real estate agent in El Paso, Texas. Learn what to look for, questions to ask, and how to get top dollar for your property.",
          author: {
            "@type": "Organization",
            name: "TREX RE LLC",
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section with Lead Capture Form */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/f5f5802777c2251f8a292d0287012654.jpg)`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-500/90 text-white text-sm font-semibold rounded-full mb-6">
                Free Realtor Referral Service
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Looking to Hire a <span className="text-teal-400">Realtor</span> in El Paso?
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                If your home is in sale-ready condition and you want top dollar, a professional realtor is your best path to maximum value. We'll connect you with trusted agents we've worked with for years.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Vetted Local Agents</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>No Obligation</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>100% Free Service</span>
                </div>
              </div>
            </div>

            <div>
              <LeadCaptureForm 
                formId="hero-realtor-form"
                title="Get Matched with a Top Realtor"
                subtitle="Fill out the form below and we'll connect you with an experienced El Paso agent"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Selling a home in El Paso is one of the most significant financial decisions you'll make. Whether you're relocating for work, upgrading to a larger home, downsizing for retirement, or simply ready for a change, the question of whether to hire a realtor—and which one to choose—can feel overwhelming. This comprehensive guide will help you understand when hiring a realtor makes sense, what to look for in an agent, and how to ensure you get the best possible outcome for your property sale.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At TREX RE LLC, we're real estate investors who buy properties directly from homeowners. Because we're investors, we can't pay full retail price for homes—we need to purchase at a discount to make our business model work. However, we recognize that not every homeowner's situation is right for a cash sale to an investor. If your property is in excellent condition, you have time to wait for the right buyer, and you want to maximize your sale price, working with a professional realtor is absolutely the best path forward.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Over the years, we've built relationships with dozens of outstanding El Paso realtors through our investment activities. We've seen firsthand which agents deliver results, communicate effectively, and truly advocate for their clients. That's why we're happy to refer homeowners to these trusted professionals when a traditional sale makes more sense than selling to us directly.
          </p>
        </div>

        {/* Why Hire a Realtor Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Why Hire a Realtor to Sell Your El Paso Home?
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=professional%20female%20real%20estate%20agent%20in%20business%20attire%20showing%20property%20details%20on%20tablet%20to%20interested%20buyers%20in%20modern%20el%20paso%20home%20interior%20with%20large%20windows%20and%20natural%20light%20professional%20photography%20warm%20tones&width=800&height=500&seq=hire-realtor-why-002&orientation=landscape"
            alt="Real estate agent showing property to buyers"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Real estate agents bring expertise, market knowledge, and negotiation skills that can significantly impact your final sale price. While some homeowners successfully sell on their own (known as For Sale By Owner or FSBO), statistics consistently show that agent-assisted sales typically result in higher sale prices—often enough to more than offset the commission costs.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Market Expertise and Pricing Strategy
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            One of the most valuable services a realtor provides is accurate pricing. Pricing your home correctly from the start is crucial—price too high and your home sits on the market, becoming "stale" in buyers' eyes; price too low and you leave money on the table. Experienced El Paso realtors understand the nuances of different neighborhoods, from the established communities of the West Side to the growing developments in the Northeast near Fort Bliss.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A skilled agent will conduct a thorough Comparative Market Analysis (CMA), examining recent sales of similar properties, current market conditions, and unique features of your home. They understand how factors like proximity to Fort Bliss, school district ratings, mountain views, and neighborhood amenities affect value. This expertise is particularly valuable in El Paso's diverse market, where prices can vary significantly between areas. If you're curious about the FSBO route, you can learn more in our guide on{' '}
            <a
              href="/elpaso-tx/sell-house-without-agent"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              how to sell your house without an agent in El Paso
            </a>
            .
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Professional Marketing and Exposure
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Today's home buyers start their search online, and professional marketing can make or break your sale. Top realtors invest in professional photography, virtual tours, drone footage, and compelling listing descriptions that showcase your home's best features. They have access to the Multiple Listing Service (MLS), which syndicates your listing to hundreds of real estate websites and exposes your property to thousands of potential buyers.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Beyond online marketing, experienced agents leverage their professional networks, host open houses, and actively market to other agents who may have qualified buyers. This comprehensive approach ensures maximum exposure for your property, which typically translates to more showings, more offers, and ultimately a better sale price.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Negotiation Skills and Transaction Management
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Negotiating a real estate transaction involves much more than agreeing on a price. There are inspection contingencies, financing terms, closing costs, repair requests, and countless other details that can make or break a deal. Experienced realtors have negotiated hundreds of transactions and know how to advocate for your interests while keeping deals together.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            They also manage the complex paperwork and legal requirements involved in Texas real estate transactions. From the initial listing agreement through closing, there are numerous documents, disclosures, and deadlines to track. A good agent ensures nothing falls through the cracks, protecting you from potential legal issues down the road. This is especially important if you're dealing with complications like{' '}
            <a
              href="/title-issues"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              title issues
            </a>{' '}
            that require professional handling.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">
              Key Benefits of Hiring a Realtor
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900">Accurate Pricing</p>
                  <p className="text-gray-600 text-sm">Data-driven pricing strategy to maximize your sale price</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900">MLS Access</p>
                  <p className="text-gray-600 text-sm">Exposure to thousands of buyers and agents</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900">Professional Marketing</p>
                  <p className="text-gray-600 text-sm">High-quality photos, virtual tours, and targeted advertising</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900">Expert Negotiation</p>
                  <p className="text-gray-600 text-sm">Skilled advocacy to get you the best terms</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900">Transaction Management</p>
                  <p className="text-gray-600 text-sm">Handle paperwork, deadlines, and legal requirements</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900">Network Access</p>
                  <p className="text-gray-600 text-sm">Connections to lenders, inspectors, and contractors</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* When a Realtor is the Right Choice */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            When Hiring a Realtor is the Right Choice
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            While we buy houses directly from homeowners, we're the first to admit that our service isn't right for everyone. Here are the situations where hiring a realtor typically makes the most sense:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Your Home is in Sale-Ready Condition
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If your property is well-maintained, updated, and ready for buyers to move in, you're positioned to command top dollar on the open market. Homes in excellent condition attract more buyers, generate more offers, and typically sell for higher prices. A realtor can help you showcase these qualities and attract qualified buyers willing to pay premium prices.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Sale-ready homes typically feature updated kitchens and bathrooms, modern flooring, fresh paint, functioning systems (HVAC, plumbing, electrical), and good curb appeal. If your home checks these boxes, the traditional market is likely your best bet for maximizing value.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            You Have Time to Wait for the Right Buyer
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Traditional home sales take time. From listing to closing, expect 60-90 days or more in most markets. If you're not in a rush and can accommodate showings, open houses, and the back-and-forth of negotiations, you'll likely achieve a higher sale price than a quick cash sale. Realtors excel at managing this process and keeping transactions on track.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            However, if you're facing time-sensitive situations like job relocation, divorce, or{' '}
            <a
              href="/avoid-foreclosure"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              foreclosure
            </a>
            , the traditional timeline may not work for your circumstances. In these cases, a cash sale might be worth considering despite the lower price.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            You Want Maximum Market Value
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If getting the highest possible price is your primary goal and you're willing to invest time and effort into the process, a realtor is your best partner. They'll help you prepare your home for sale, price it strategically, market it effectively, and negotiate the best possible terms. While you'll pay a commission (typically 5-6% of the sale price), the higher sale price often more than compensates for this cost.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            You Have Resources for Repairs and Staging
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            To achieve top dollar, most homes benefit from some level of preparation—whether that's minor repairs, fresh paint, professional cleaning, or staging. If you have the financial resources and time to invest in these improvements, you'll see returns in your final sale price. Realtors can advise on which improvements offer the best return on investment for El Paso buyers.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=beautifully%20staged%20modern%20living%20room%20interior%20with%20southwestern%20decor%20neutral%20colors%20comfortable%20furniture%20natural%20light%20streaming%20through%20windows%20professional%20real%20estate%20photography%20showing%20home%20ready%20for%20sale%20in%20el%20paso&width=800&height=500&seq=hire-realtor-staged-003&orientation=landscape"
            alt="Professionally staged home interior"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  Ready to Find Your Perfect Realtor?
                </h2>
                <p className="text-gray-300 mb-6">
                  We've worked with El Paso's top real estate agents for years. Let us connect you with a trusted professional who will fight for your best interests and help you get top dollar for your home.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Experienced local agents only</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>No cost or obligation to you</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Matched to your specific needs</span>
                  </li>
                </ul>
              </div>
              <div>
                <LeadCaptureForm 
                  formId="middle-realtor-form"
                  title="Get Your Free Referral"
                  subtitle="Connect with a top El Paso realtor today"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What to Look for in an El Paso Realtor */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What to Look for When Hiring an El Paso Realtor
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Not all real estate agents are created equal. The difference between a mediocre agent and an exceptional one can mean tens of thousands of dollars in your pocket. Here's what to look for when evaluating potential realtors:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Local Market Knowledge
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            El Paso's real estate market has unique characteristics that out-of-area agents simply don't understand. Look for an agent who knows the differences between neighborhoods, understands the impact of Fort Bliss on the market, and can speak knowledgeably about local schools, amenities, and development trends. They should be familiar with resources like the{' '}
            <a
              href="/elpaso-tx/epcad"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              El Paso Central Appraisal District (EPCAD)
            </a>{' '}
            and understand local property tax implications.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Ask potential agents about recent sales in your neighborhood, current market trends, and their assessment of your home's value. A knowledgeable agent will provide specific, data-backed answers rather than vague generalities.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Proven Track Record
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Experience matters in real estate. Look for agents with a strong track record of successful sales in your area and price range. Ask about their average days on market, list-to-sale price ratio, and total transaction volume. Top agents typically sell homes faster and closer to asking price than less experienced competitors.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Don't be afraid to ask for references from past clients. A confident agent will happily provide contacts who can speak to their professionalism, communication, and results.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Marketing Capabilities
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In today's digital-first market, marketing quality directly impacts sale outcomes. Ask potential agents about their marketing plan for your property. Do they use professional photography? Virtual tours? Drone footage? Social media advertising? A comprehensive marketing strategy should include:
          </p>

          <ul className="list-disc pl-8 mb-6 text-lg text-gray-700 space-y-3">
            <li>Professional photography and videography</li>
            <li>Virtual tours and 3D walkthroughs</li>
            <li>MLS listing with detailed descriptions</li>
            <li>Syndication to major real estate websites</li>
            <li>Social media marketing and targeted advertising</li>
            <li>Email marketing to buyer agents</li>
            <li>Open houses and broker tours</li>
            <li>Print materials and signage</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Communication Style
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Selling a home can be stressful, and good communication makes all the difference. During your initial conversations, pay attention to how responsive the agent is, how clearly they explain things, and whether they listen to your concerns. Ask about their preferred communication methods and how often you can expect updates.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The best agents are proactive communicators who keep you informed throughout the process, not just when there's a problem. They should be available to answer questions and address concerns promptly.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Professional Network
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Experienced agents have built networks of trusted professionals—home inspectors, contractors, lenders, title companies, and other agents. These connections can smooth the transaction process and help resolve issues quickly. Ask potential agents about their professional relationships and how they can help facilitate your sale.
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-error-warning-line mr-2 text-amber-500"></i>
              Questions to Ask Potential Realtors
            </h4>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>How long have you been selling real estate in El Paso?</li>
              <li>How many homes did you sell last year? What was your average days on market?</li>
              <li>What's your marketing plan for my property?</li>
              <li>How do you determine the right listing price?</li>
              <li>How will you communicate with me throughout the process?</li>
              <li>Can you provide references from recent clients?</li>
              <li>What sets you apart from other agents?</li>
              <li>What's your commission structure?</li>
            </ul>
          </div>
        </section>

        {/* Understanding the El Paso Market */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Understanding the El Paso Real Estate Market
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=aerial%20panoramic%20view%20of%20el%20paso%20texas%20cityscape%20with%20diverse%20residential%20neighborhoods%20franklin%20mountains%20in%20background%20showing%20urban%20development%20and%20housing%20variety%20clear%20blue%20sky%20professional%20drone%20photography&width=800&height=500&seq=hire-realtor-market-004&orientation=landscape"
            alt="El Paso cityscape aerial view"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            El Paso's real estate market has unique characteristics that both sellers and their agents need to understand. As the sixth-largest city in Texas and a major border community, El Paso offers diverse housing options and a relatively affordable cost of living compared to other Texas metros.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Military Influence on the Market
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Fort Bliss, one of the largest military installations in the United States, significantly impacts El Paso's housing market. The constant rotation of military personnel creates steady demand for housing, particularly in the Northeast and East Side areas closest to the base. This military presence provides market stability but also creates seasonal patterns, with peak buying activity during summer PCS (Permanent Change of Station) season.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Experienced El Paso realtors understand how to market to military buyers, including familiarity with VA loans, BAH (Basic Allowance for Housing) rates, and the unique needs of military families. If your home is in a military-friendly area, this expertise can be particularly valuable.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Neighborhood Diversity
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            El Paso's neighborhoods vary dramatically in character, price point, and buyer demographics. From the established, higher-end communities of the Upper Valley and West Side to the growing master-planned developments in the Northeast, each area attracts different buyer profiles. Understanding these distinctions helps agents target marketing efforts and set appropriate pricing expectations.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">West Side / Upper Valley</h4>
              <p className="text-gray-700 text-sm">
                Premium neighborhoods with established homes, excellent schools, and mountain views. Attracts professionals and families seeking quality of life. Higher price points with discerning buyers.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Northeast El Paso</h4>
              <p className="text-gray-700 text-sm">
                Newer construction and master-planned communities. Strong military buyer presence due to Fort Bliss proximity. Family-oriented with good schools and amenities.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Central / Downtown</h4>
              <p className="text-gray-700 text-sm">
                Historic properties and urban living. Attracts young professionals, investors, and those seeking walkable neighborhoods. Mix of renovation opportunities and updated homes.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">East Side / Lower Valley</h4>
              <p className="text-gray-700 text-sm">
                More affordable options appealing to first-time buyers and investors. Strong sense of community with established neighborhoods. Value-conscious buyer market.
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Current Market Conditions
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            El Paso has experienced steady appreciation in home values, though at a more moderate pace than some other Texas cities. This stability makes pricing more predictable but also means sellers need realistic expectations. A good realtor will provide current market data and help you understand how your home compares to recent sales.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Factors currently influencing the El Paso market include interest rates, military deployment cycles, cross-border economic activity, and local employment trends. Staying informed about{' '}
            <a
              href="/local-news-el-paso-texas"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              local El Paso news and developments
            </a>{' '}
            can help you make better decisions about timing and pricing your sale.
          </p>
        </section>

        {/* The Selling Process with a Realtor */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Home Selling Process with a Realtor
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Understanding what to expect when working with a realtor helps you prepare for the journey ahead. Here's a typical timeline and process for selling your El Paso home with professional representation:
          </p>

          <div className="space-y-8 mb-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full">
                <span className="text-teal-600 font-bold">1</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Initial Consultation (Week 1)</h4>
                <p className="text-gray-700">
                  Meet with potential agents, tour your home, discuss your goals and timeline. The agent will provide a preliminary market analysis and explain their services and commission structure.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full">
                <span className="text-teal-600 font-bold">2</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Preparation Phase (Weeks 1-3)</h4>
                <p className="text-gray-700">
                  Complete any recommended repairs or improvements. Professional cleaning, staging consultation, and photography scheduling. Agent prepares marketing materials and listing strategy.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full">
                <span className="text-teal-600 font-bold">3</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Listing Goes Live (Week 3-4)</h4>
                <p className="text-gray-700">
                  Home is listed on MLS and syndicated to real estate websites. Marketing campaign launches. Open houses and showings begin. Agent provides feedback from buyer agents.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full">
                <span className="text-teal-600 font-bold">4</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Offers and Negotiations (Weeks 4-8)</h4>
                <p className="text-gray-700">
                  Receive and review offers with your agent. Negotiate terms including price, contingencies, and closing timeline. Accept an offer and enter contract.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full">
                <span className="text-teal-600 font-bold">5</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Under Contract (Weeks 8-12)</h4>
                <p className="text-gray-700">
                  Buyer conducts inspections and appraisal. Negotiate any repair requests. Buyer finalizes financing. Title company prepares closing documents.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full">
                <span className="text-teal-600 font-bold">6</span>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Closing (Week 12+)</h4>
                <p className="text-gray-700">
                  Final walkthrough with buyer. Sign closing documents at title company. Receive your proceeds. Hand over keys to new owner.
                </p>
              </div>
            </div>
          </div>

          <img
            src="https://readdy.ai/api/search-image?query=happy%20couple%20signing%20real%20estate%20closing%20documents%20at%20title%20company%20office%20with%20real%20estate%20agent%20and%20closing%20officer%20professional%20business%20setting%20warm%20lighting%20celebrating%20home%20sale&width=800&height=500&seq=hire-realtor-closing-005&orientation=landscape"
            alt="Home closing signing documents"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />
        </section>

        {/* When a Cash Sale Might Be Better */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            When a Cash Sale Might Be a Better Option
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            While we believe realtors are the best choice for many sellers, we also recognize that some situations call for a different approach. As real estate investors, we buy homes directly from owners in situations where the traditional market isn't ideal. Here's when selling to a cash buyer like TREX RE LLC might make more sense:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-600">
              <h4 className="font-bold text-gray-900 mb-2">Property Needs Major Repairs</h4>
              <p className="text-gray-700 text-sm">
                If your home needs significant work—roof replacement, foundation issues, outdated systems—the cost of repairs may not be worth the investment. We buy as-is.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-600">
              <h4 className="font-bold text-gray-900 mb-2">You Need to Sell Quickly</h4>
              <p className="text-gray-700 text-sm">
                Job relocation, divorce, financial hardship, or other time-sensitive situations may require a faster sale than the traditional market allows.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-600">
              <h4 className="font-bold text-gray-900 mb-2">Inherited Property</h4>
              <p className="text-gray-700 text-sm">
                <a href="/inherited-homes" className="text-teal-600 hover:text-teal-700">Inherited homes</a> often come with complications—deferred maintenance, out-of-state ownership, multiple heirs. A cash sale simplifies the process.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-600">
              <h4 className="font-bold text-gray-900 mb-2">Facing Foreclosure</h4>
              <p className="text-gray-700 text-sm">
                If you're behind on payments and facing foreclosure, a quick cash sale can help you avoid the credit damage and get out from under the burden.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-600">
              <h4 className="font-bold text-gray-900 mb-2">Difficult Tenants</h4>
              <p className="text-gray-700 text-sm">
                Selling a <a href="/rental-property" className="text-teal-600 hover:text-teal-700">rental property</a> with problem tenants can be challenging on the open market. We buy occupied properties.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-600">
              <h4 className="font-bold text-gray-900 mb-2">You Want Certainty</h4>
              <p className="text-gray-700 text-sm">
                Traditional sales can fall through due to financing issues, inspection problems, or buyer cold feet. Cash sales offer certainty of closing.
              </p>
            </div>
          </div>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              Not Sure Which Option is Right for You?
            </h4>
            <p className="text-gray-700 mb-4">
              We're happy to discuss your situation and give you an honest assessment. If a realtor is your best bet, we'll tell you—and connect you with a great one. If a cash sale makes more sense, we'll make you a fair offer. Either way, you'll have the information you need to make the right decision.
            </p>
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814 for a Free Consultation
            </a>
          </div>
        </section>

        {/* Commission and Costs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Understanding Realtor Commissions and Selling Costs
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            One of the most common questions sellers have is about costs. Understanding the financial aspects of selling with a realtor helps you make informed decisions and set realistic expectations for your net proceeds.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            How Realtor Commissions Work
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In most traditional sales, the seller pays a commission that's split between the listing agent and the buyer's agent. In El Paso, total commissions typically range from 5% to 6% of the sale price. On a $300,000 home, that's $15,000 to $18,000. This commission is paid at closing from the sale proceeds—you don't pay anything upfront.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            While commissions are negotiable, be cautious about choosing an agent solely based on a lower commission rate. A skilled agent who sells your home for $10,000 more than a discount agent is worth the extra commission cost. Focus on value and results rather than just the percentage.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Other Selling Costs to Consider
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Beyond commission, sellers typically pay several other costs at closing:
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">Typical Seller Closing Costs in El Paso</h4>
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span>Title Insurance (Owner's Policy)</span>
                <span className="font-semibold">$1,500 - $3,000</span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span>Escrow/Closing Fees</span>
                <span className="font-semibold">$500 - $1,000</span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span>Property Taxes (Prorated)</span>
                <span className="font-semibold">Varies</span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span>HOA Transfer Fees (if applicable)</span>
                <span className="font-semibold">$200 - $500</span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span>Home Warranty (if offered)</span>
                <span className="font-semibold">$400 - $600</span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-2">
                <span>Repair Credits (negotiated)</span>
                <span className="font-semibold">Varies</span>
              </div>
              <div className="flex justify-between pt-2">
                <span className="font-bold">Total (excluding commission)</span>
                <span className="font-bold text-teal-600">$3,000 - $6,000+</span>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Your realtor can provide a detailed estimate of your net proceeds based on your specific situation, including any outstanding mortgage balance, property taxes owed, and anticipated closing costs.
          </p>
        </section>

        {/* Comparison Table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Comparing Your Selling Options
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            To help you decide which selling method is right for your situation, here's a comprehensive comparison:
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-4 text-left">Factor</th>
                  <th className="p-4 text-left">With Realtor</th>
                  <th className="p-4 text-left">FSBO</th>
                  <th className="p-4 text-left">Cash Buyer</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b">
                  <td className="p-4 font-medium">Sale Price</td>
                  <td className="p-4">Highest (market value)</td>
                  <td className="p-4">Market value (if priced right)</td>
                  <td className="p-4">Below market (convenience trade-off)</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Commission/Fees</td>
                  <td className="p-4">5-6%</td>
                  <td className="p-4">0-3%</td>
                  <td className="p-4">0%</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Time to Sell</td>
                  <td className="p-4">60-90+ days</td>
                  <td className="p-4">60-120+ days</td>
                  <td className="p-4">7-14 days</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Repairs Needed</td>
                  <td className="p-4">Recommended for best price</td>
                  <td className="p-4">Recommended for best price</td>
                  <td className="p-4">None required</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Showings</td>
                  <td className="p-4">Multiple (agent handles)</td>
                  <td className="p-4">Multiple (you handle)</td>
                  <td className="p-4">One visit only</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Closing Certainty</td>
                  <td className="p-4">Medium (financing risk)</td>
                  <td className="p-4">Medium (financing risk)</td>
                  <td className="p-4">High (cash, no contingencies)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Your Effort</td>
                  <td className="p-4">Low</td>
                  <td className="p-4">High</td>
                  <td className="p-4">Very Low</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-medium">Best For</td>
                  <td className="p-4">Sale-ready homes, max value</td>
                  <td className="p-4">Experienced sellers with time</td>
                  <td className="p-4">Quick sales, problem properties</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About Hiring a Realtor
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How do I find a good realtor in El Paso?
              </h3>
              <p className="text-gray-700">
                Ask for referrals from friends and family, check online reviews, and interview multiple agents. Look for experience in your neighborhood, strong marketing capabilities, and good communication skills. Or let us connect you with a trusted agent we've worked with—fill out the form above for a free referral.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What's the average realtor commission in El Paso?
              </h3>
              <p className="text-gray-700">
                Total commission typically ranges from 5% to 6% of the sale price, split between the listing agent and buyer's agent. Commissions are negotiable, but focus on value and results rather than just the percentage.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How long does it take to sell a house with a realtor?
              </h3>
              <p className="text-gray-700">
                In El Paso, properly priced homes typically sell within 30-60 days, with another 30-45 days to close. Total timeline from listing to closing is usually 60-90 days, though this varies based on market conditions and property specifics.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Should I make repairs before listing with a realtor?
              </h3>
              <p className="text-gray-700">
                Generally yes—homes in good condition sell faster and for higher prices. Your agent can advise on which repairs offer the best return on investment. However, if repairs are extensive, selling as-is to a cash buyer might be more practical.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I cancel my listing agreement if I'm unhappy with my realtor?
              </h3>
              <p className="text-gray-700">
                Listing agreements are contracts, so review the terms carefully before signing. Most agreements include a termination clause, though there may be conditions or notice requirements. Discuss any concerns with your agent first—good agents want satisfied clients.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if my house doesn't sell with a realtor?
              </h3>
              <p className="text-gray-700">
                If your home isn't selling, work with your agent to identify the issue—usually pricing, condition, or marketing. You may need to adjust your price or make improvements. If the traditional market isn't working, a cash sale to an investor is always an option.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Sell Your El Paso Home?
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Whether you want top dollar through a realtor or need a fast cash sale, we're here to help you find the right path forward.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <LeadCaptureForm 
                formId="final-realtor-form"
                title="Get Your Free Realtor Referral"
                subtitle="We'll connect you with a top El Paso agent within 24 hours"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">
                Or if you'd prefer to explore a cash offer:
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="tel:+1-915-206-5814"
                  className="inline-flex items-center px-6 py-3 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (915) 206-5814
                </a>
                <a
                  href="/get-cash-offer-for-my-property"
                  className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get a Cash Offer Instead
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
